import React, {useEffect, useRef} from "react";
import {useFormik} from "formik";
import {useAlert} from 'react-alert';
import {useHistory} from "react-router-dom";
import Loader from "../common/views/Loader";
import {AppVersionSchema, InitialAppVersionValues} from "../common/types/Schemas";
import {BYInput} from "../common/inputs/BYInput";
import {useAppVersion, useUpdateAppVersion} from "../api/hooks";
import {Animated} from "react-animated-css";
import Footer from "../common/views/Footer";
import BYSwitch from "../common/inputs/BYSwitch";
import BYDatePicker from "../common/inputs/BYDatePicker";

interface AppVersionIndexProps {
    match: {
        params: {
            id: string;
        }
    }
}

export const AppVersionIndex = (props: AppVersionIndexProps) => {
    const alert = useAlert();
    const history = useHistory();
    const ipaInputRef = useRef<HTMLInputElement>(null);
    const appVersionId = Number(props.match.params.id);
    const isNew = props.match.params.id === 'new';

    const {mutateAsync: mutateAppVersion} = useUpdateAppVersion();
    const {data: appVersion, isLoading} = useAppVersion(appVersionId);

    const formik = useFormik({
        initialValues: InitialAppVersionValues,
        validationSchema: AppVersionSchema,
        onSubmit: async (values) => {
            try {
                const ipaFile = ipaInputRef.current.files[0];

                const formData = Object.keys(values).reduce((formData, key) => {
                    formData.append(key, values[key]);
                    return formData;
                }, new FormData());

                if (ipaFile) {
                    formData.append('image', ipaFile, ipaFile.name);
                }

                await mutateAppVersion({appVersionId, formData});

                alert.success(`Version d'application mise à jour`);

                history.goBack();
            } catch (error) {
                const statusCode = error.response && error.response.status;

                if (statusCode === 401) {
                    alert.error('Valeurs invalides ou incomplètes');
                } else if (statusCode === 409) {
                    alert.error(error.response.data);
                } else {
                    alert.error(error.message);
                }
            }
        }
    });

    useEffect(() => {
        if (appVersion) {

            formik.setValues({
                description: appVersion.description,
                disclaimer: appVersion.disclaimer,
                mandatorySince: appVersion.mandatorySince,
                active: appVersion.active,
                file: undefined
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appVersion]);

    if (isLoading) {
        return <div className="text-center my-6"><Loader/></div>;
    }

    return (
        <Animated animationIn="fadeIn" animationOut="fadeOut" isVisible={true}>
            <div className="container">
                <form onSubmit={formik.handleSubmit} autoComplete="new-password">
                    <div className="row">
                        <div className="col-8">
                            <h4 className="my-4 color-green">{isNew ? 'Création de nouvelle version' : 'Modification de version'}</h4>
                        </div>
                        <div className="col-4 tittle-buttons">
                            <BYSwitch
                                title={'Active'}
                                checked={formik.values.active}
                                onChange={(newValue) => {
                                    formik.setValues({
                                        ...formik.values,
                                        active: newValue
                                    });
                                }}
                            />
                        </div>
                    </div>
                    <div className="input-container">
                        <div className="row mb-3">
                            <div className="col">
                                <BYInput
                                    type="text"
                                    name="description"
                                    title="Nom de version (par ex. 1.0.23) *"
                                    className="form-control"
                                    value={formik.values.description}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    required
                                    autoFocus
                                    touched={formik.touched.description}
                                    error={formik.errors.description}
                                    helpMessage="Utilisez le même nom de version sur l'écran d'authentification pour comparaison et affichage de message de téléchargement."
                                />
                            </div>
                            <div className="col">
                                <label className="form-label">{isNew ? 'Fichier version *' : 'Fichier version'}</label>
                                <input
                                    ref={ipaInputRef}
                                    type="file"
                                    name="file"
                                    accept=".ipa"
                                    className="form-control"
                                    onBlur={formik.handleBlur}
                                    onChange={(e) => {

                                    }}
                                    required={isNew}
                                />
                                {formik.touched.file && formik.errors.file && <div className="text-danger font-weight-bold">{formik.errors.file}</div>}
                            </div>
                        </div>


                        <div className="row mb-3">
                            <div className="col">
                                <BYInput
                                    type="textarea"
                                    name="disclaimer"
                                    title="Message d'installation *"
                                    className="form-control"
                                    value={formik.values.disclaimer}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    touched={formik.touched.disclaimer}
                                    error={formik.errors.disclaimer}
                                />
                            </div>
                            <div className="col">
                                <BYDatePicker
                                    name="mandatorySince"
                                    title="Installer à partir du *"
                                    value={formik.values.mandatorySince}
                                    onChange={(newValue: Date) => {
                                        formik.setValues({
                                            ...formik.values,
                                            mandatorySince: newValue?.toISOString()
                                        })
                                    }}
                                    touched={formik.touched.mandatorySince}
                                    error={formik.errors.mandatorySince}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="d-flex my-5">
                        <button className="btn btn-secondary flex-grow-1" type="submit"
                                disabled={!formik.isValid || formik.isSubmitting}>
                            Sauvegarder{' '}
                            <i className="fa fa-save"/>
                        </button>
                    </div>
                </form>
            </div>
            <Footer/>
        </Animated>
    );
};
